import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetBranchList } from '~/usecases/useGetBranchList';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { useDeleteBranch } from './useDeleteBranch';

type Branch = {
  id: number;
  name: string;
  code: string;
};

type IUseBranchList = () => {
  state: {
    data: ReturnType<typeof useGetBranchList>['data'];
    currentPage: number;
    totalPage: number;
    totalCount: number;
  };
  actions: {
    handlePageChange: (page: number) => void;
    handleEditBranch: (branchId: string) => void;
    handleDeleteBranch: (branchId: string) => void;
  };
};

export const useBranchList: IUseBranchList = () => {
  const [filterParams, setFilterParams] = useSearchParams();
  const threeLetterCode = getThreeLetterCode();
  const page = filterParams.get('page') ?? '1';
  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();
  const deletingBranchRef = useRef<Branch | null>(null);
  const navigate = useNavigate();

  const { data, totalPage, totalCount, mutate } = useGetBranchList({ threeLetterCode, page });

  const { trigger: deleteBranch } = useDeleteBranch(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: `「${deletingBranchRef.current?.name}」を削除しました。`,
        status: 'success',
      });
      mutate();
    },
    onError: () => {
      showToast({
        message: '削除に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', page.toString());
    setFilterParams(newParams);
  };

  const handleEditBranch = (branchId: string) => {
    navigate(replaceRouteParams(ROUTES.BRANCHES.EDIT, { id: Number(branchId) }));
  };

  const handleDeleteBranch = (branchId: string) => {
    const branch = data.branches.find(b => b.id === Number(branchId));
    if (!branch) return;

    deletingBranchRef.current = branch;

    showAlertDialog({
      title: `「${branch.name}」を削除しますか？`,
      message: `「${branch.name}」（事業所コード：${branch.code}）を削除します。削除すると元に戻すことはできません。`,
      confirmText: '削除',
      confirmButtonVariant: 'destructive',
      onConfirm: async () => {
        await deleteBranch(branch.id);
      },
    });
  };

  return {
    state: {
      data,
      currentPage: Number(page),
      totalPage,
      totalCount,
    },
    actions: {
      handlePageChange,
      handleEditBranch,
      handleDeleteBranch,
    },
  };
};
